// packages/supabase-client/src/react/forms/SignInForm.tsx (Correct and Complete)

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { Button, TextField } from '@dopomogai/ui';

// Import types and validators from within the package
import { validateEmail, validateRequired } from '../utils/validation';
import { FormErrors, AuthFormData } from '../types/form';
import { useAuthText, useAuthUI } from '../hooks';
import { AuthAlert } from '../components';

/**
 * The props for the reusable SignInForm component.
 */
export interface SignInFormProps {
  /**
   * Callback to execute when the form is submitted with valid data.
   * The parent component is responsible for handling the submission logic.
   */
  onSubmit: (data: Pick<AuthFormData, 'email' | 'password'>) => Promise<void>;
  /**
   * Prop to control the loading state of the submit button from the parent.
   */
  isPending?: boolean;
  /**
   * Prop to display a general error message (e.g., from an API call) from the parent.
   */
  errorMessage?: React.ReactNode | null;
}

export const SignInForm: React.FC<SignInFormProps> = ({ onSubmit, isPending, errorMessage }) => {
  const { t } = useTranslation(['auth', 'common']);
  const { text, resolveText, resolveString } = useAuthText();
  const { routes } = useAuthUI();
  const signInText = resolveText(text.signInButton, 'auth:signIn');
  const signInTextIsString = typeof signInText === 'string' || typeof signInText === 'number';
  
  // Internal state for form data, validation errors, and password visibility
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [errors, setErrors] = useState<FormErrors>({});
  const [showPassword, setShowPassword] = useState(false);

  // This function remains the same, handling local state updates.
  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  // This function remains the same, handling local field validation.
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    const emailValidationError = validateEmail(formData.email);
    const passwordValidationError = validateRequired(formData.password);

    if (emailValidationError) {
      newErrors.email = t(emailValidationError, { ns: 'common' });
    }
    if (passwordValidationError) {
      newErrors.password = t(passwordValidationError, { ns: 'common' });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // --- MODIFIED SUBMISSION HANDLER ---
  // It no longer contains try/catch, navigation, or calls signIn directly.
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    // The form's only job is to call the onSubmit prop.
    // The parent (`LoginPage`) will handle the API call and its outcome.
    await onSubmit(formData);
  };

  // --- THE UI REMAINS IDENTICAL ---
  // It is now controlled by props (`isPending`, `errorMessage`) instead of internal state.
  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {errorMessage && <AuthAlert message={errorMessage} />}

      <TextField 
        label={resolveString(text.emailLabel, 'common:email')} 
        value={formData.email} 
        onChange={(e) => handleInputChange('email', e.target.value)} 
        placeholder={resolveString(text.emailPlaceholder, 'auth:emailPlaceholder')} 
        leadingIcon={<Mail className="w-5 h-5 text-slate-400" />} 
        errorText={errors.email} 
        disabled={isPending} 
      />
      
      <TextField 
        label={resolveString(text.passwordLabel, 'common:password')} 
        type={showPassword ? 'text' : 'password'} 
        value={formData.password} 
        onChange={(e) => handleInputChange('password', e.target.value)} 
        placeholder={resolveString(text.passwordPlaceholder, 'auth:passwordPlaceholder')}
        leadingIcon={<Lock className="w-5 h-5 text-slate-400" />}
        trailingIcon={
          <button type="button" onClick={() => setShowPassword(!showPassword)} className="text-slate-400 hover:text-slate-600 focus:outline-none" aria-label={t('togglePasswordVisibility', { ns: 'auth', defaultValue: 'Toggle password visibility' })}>
            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
        }
        errorText={errors.password} 
        disabled={isPending}
      />
      
      <div className="flex items-center justify-between">
        <div></div> {/* Empty div for alignment if needed */}
        <Link to={routes.forgotPassword} className="text-emerald-600 hover:text-emerald-700 text-sm font-medium transition-colors">
          {resolveText(text.forgotPasswordLink, 'auth:forgotPassword')}
        </Link>
      </div>
      
      <Button 
        type="submit" 
        variant="primary" 
        fullWidth 
        text={signInTextIsString ? String(signInText) : undefined}
        disabled={isPending} 
        loading={isPending}
      >
        {!signInTextIsString ? signInText : null}
      </Button>
      
      <div className="text-center">
        <span className="text-slate-600">{resolveText(text.noAccount, 'auth:noAccount')} </span>
        <Link to={routes.register} className="text-emerald-600 hover:text-emerald-700 font-medium transition-colors">
          {resolveText(text.signUpLink, 'auth:signUp')}
        </Link>
      </div>
    </form>
  );
};
